import React, { useEffect, useRef, useState } from 'react';
import {
	isRedirectable,
	truncateAddress,
	useWallet,
	WalletReadyState,
} from '@aptos-labs/wallet-adapter-react';
import { useMutation } from '@tanstack/react-query';
import { Button, type ButtonProps } from '@mui/material';
import useLogout from '../hooks/useLogout';
import { useUserContext } from 'context/userContext';
import { fetchSignInInput, loginWithSignInOutput } from 'lib/auth';
import { createOrGetUser } from 'api/client';
import {
	notifySlackError,
	notifySlackSuccess,
} from 'utils/slack/slackNotifier';
import analytics from 'utils/segment';
import { canonicalWalletName } from 'utils/wallets';

declare enum UserResponseStatus {
	APPROVED = 'Approved',
	REJECTED = 'Rejected',
}

interface ConnectButtonProps extends ButtonProps {
	siwaVariant?: 'default' | 'error';
	walletName?: string;
	walletKey?: string;
	onSuccess?: () => void;
	onLoginStart?: () => void;
	onLoginEnd?: () => void;
}

type LoginResult = {
	walletAddress: string;
	userId: string;
	actualWalletName: string;
};

type SignInInputPayload = {
	nonce: string;
	domain: string;
	uri: string;
	statement: string;
};

export default function ConnectButton({
	walletName = 'Petra',
	walletKey = 'Petra',
	onSuccess,
	onLoginStart,
	onLoginEnd,
	...props
}: ConnectButtonProps) {
	const { walletAddress, isLoggedIn, setUserId, baseUrl } = useUserContext();
	const { mutate: logout } = useLogout();
	const [hasPrefetchedInput, setHasPrefetchedInput] = useState(false);

	// include notDetectedWallets so mobile deep-link wallets (Petra / Google)
	// are also available for SIWA
	const {
		signIn,
		connect: connectWallet,
		disconnect,
		wallets = [],
		notDetectedWallets = [],
	} = useWallet();

	const cachedInputRef = useRef<SignInInputPayload | null>(null);
	const normalizedWalletName = canonicalWalletName(walletName);
	const isAptosConnectWallet =
		normalizedWalletName === 'Continue with Google' ||
		normalizedWalletName === 'Apple';
	const safariRequiresPrefetch =
		isAptosConnectWallet && !hasPrefetchedInput && !isLoggedIn;
	const walletHasSignInSupport = (wallet?: any) =>
		!!wallet?.features?.['aptos:signIn']?.signIn;
	const withForcedPromptProvider = async <T,>(
		provider: 'google' | 'apple' | null,
		action: () => Promise<T>,
	): Promise<T> => {
		if (typeof window === 'undefined' || !provider) {
			return action();
		}

		const originalOpen = window.open.bind(window);
		window.open = ((url?: string | URL, target?: string, features?: string) => {
			try {
				const isPromptUrl =
					(typeof url === 'string' &&
						url.startsWith('https://web.petra.app/prompt/')) ||
					(url instanceof URL &&
						url.origin === 'https://web.petra.app' &&
						url.pathname.startsWith('/prompt/'));

				if (isPromptUrl) {
					const parsed = new URL(
						typeof url === 'string' ? url : url.toString(),
					);
					if (!parsed.searchParams.get('provider')) {
						parsed.searchParams.set('provider', provider);
					}
					return originalOpen(parsed.toString(), target, features);
				}
			} catch {
				// Fallback to original window.open
			}

			return originalOpen(url as any, target, features);
		}) as typeof window.open;

		try {
			return await action();
		} finally {
			window.open = originalOpen as typeof window.open;
		}
	};

	const ensureWalletConnection = async (walletName: string) => {
		try {
			await connectWallet(walletName);
		} catch (error: any) {
			const message = error?.message ?? '';
			if (!message.includes('already connected')) {
				throw error;
			}
		}
	};

	const signInDirectlyThroughAdapter = async (
		wallet: any,
		input: SignInInputPayload,
	) => {
		const signInFeature = wallet?.features?.['aptos:signIn'];
		if (!signInFeature?.signIn) {
			throw new Error(
				`${
					wallet?.name ?? 'Selected'
				} wallet does not support Sign In With Aptos (SIWA)`,
			);
		}

		const response = await signInFeature.signIn(input);

		if (!response || typeof response !== 'object') {
			throw new Error('Wallet did not return a valid SIWA response');
		}

		if (response.status === UserResponseStatus.REJECTED) {
			throw new Error('User rejected the sign-in request');
		}

		return response.args;
	};

	const isNoActiveSignerError = (error: unknown) => {
		const message =
			error instanceof Error
				? error.message
				: typeof error === 'string'
				? error
				: '';
		const normalized = message.toLowerCase();
		return (
			normalized.includes('no active signer') ||
			normalized.includes('active signer not assigned') ||
			normalized.includes('active signer not set') ||
			normalized.includes('signer not assigned') ||
			(normalized.includes('active signer') &&
				normalized.includes('approval error'))
		);
	};

	const prefetchInput = async () => {
		try {
			const response = await fetchSignInInput();
			cachedInputRef.current = response.data;
			setHasPrefetchedInput(true);
		} catch (error) {
			// console.warn('Failed to prefetch SIWA input', error);
			cachedInputRef.current = null;
			setHasPrefetchedInput(false);
		}
	};

	useEffect(() => {
		void prefetchInput();
	}, []);

	const takePrefetchedInput = () => {
		if (!cachedInputRef.current) {
			return null;
		}
		const cached = cachedInputRef.current;
		cachedInputRef.current = null;
		setHasPrefetchedInput(false);
		void prefetchInput();
		return cached;
	};

	const getSignInInput = async () => {
		const cached = takePrefetchedInput();
		if (cached) {
			return cached;
		}

		const response = await fetchSignInInput();
		return response.data;
	};

	const { mutate: login, isPending } = useMutation<LoginResult, Error>({
		mutationFn: async () => {
			onLoginStart?.();

			const canonicalKey = canonicalWalletName(walletKey);
			const allWallets = [...wallets, ...(notDetectedWallets ?? [])];

			// Find the wallet requested for this button
			const wallet =
				allWallets.find(
					(w) =>
						canonicalWalletName(w.name) === canonicalKey ||
						w.name === walletKey,
				) ||
				allWallets.find(
					(w) =>
						canonicalWalletName(w.name) ===
						canonicalWalletName(walletName),
				);

			if (!wallet) {
				throw new Error(
					`${walletName} wallet not found. Please install ${walletName} wallet extension.`,
				);
			}

			const readyState = (wallet as any)?.readyState as
				| WalletReadyState
				| undefined;

			const walletCanonicalName = canonicalWalletName(wallet.name);

			/**
			 * Petra mobile deep-link:
			 * On mobile browser (Safari/Chrome, etc.) with no Petra extension,
			 * open the dApp inside Petra’s Explore tab using:
			 *   https://petra.app/explore?link=<dapp_url>
			 *
			 * Docs: https://petra.app/docs/mobile-deeplinks (Open a dApp)
			 */
			const shouldOpenInPetraExplore =
				walletCanonicalName === 'Petra' &&
				isRedirectable() && // mobile browser, not in-app
				readyState === WalletReadyState.NotDetected;

			if (shouldOpenInPetraExplore) {
				// You can change this to a canonical route if you prefer:
				// const dappUrl = `${window.location.origin}/login`;
				const dappUrl = window.location.href;

				const petraExploreUrl = `https://petra.app/explore?link=${encodeURIComponent(
					dappUrl,
				)}`;

				window.location.href = petraExploreUrl;
				onLoginEnd?.();

				// We intentionally stop here; user will come back via Petra.
				return Promise.reject(
					new Error('Redirecting to Petra Explore for mobile'),
				);
			}

			const canonicalDisplayName = canonicalWalletName(wallet.name);
			const promptProvider =
				canonicalDisplayName === 'Continue with Google'
					? 'google'
					: canonicalDisplayName === 'Apple'
					? 'apple'
					: null;
			const requiresPreConnection =
				canonicalDisplayName === 'Continue with Google' ||
				canonicalDisplayName === 'Apple';
			const shouldUseInstantInput =
				requiresPreConnection && !isLoggedIn;

			if (requiresPreConnection && !shouldUseInstantInput) {
				await ensureWalletConnection(wallet.name);
			}

			// Normal SIWA flow
			const instantInput = shouldUseInstantInput
				? takePrefetchedInput()
				: null;

			if (shouldUseInstantInput && !instantInput) {
				throw new Error(
					'Preparing secure sign-in. Please try again in a moment.',
				);
			}

			const input = instantInput ?? (await getSignInInput());

			const attemptSignIn = async () =>
				signIn({
					walletName: wallet.name,
					input,
				});

			let output;

			try {
				output = await withForcedPromptProvider(
					promptProvider,
					attemptSignIn,
				);
			} catch (error) {
				if (
					wallet &&
					isNoActiveSignerError(error) &&
					walletHasSignInSupport(wallet)
				) {
					await ensureWalletConnection(wallet.name);
					try {
						output = await withForcedPromptProvider(
							promptProvider,
							attemptSignIn,
						);
					} catch (retryError) {
						output = await withForcedPromptProvider(
							promptProvider,
							() => signInDirectlyThroughAdapter(wallet, input),
						);
					}
				} else {
					throw error;
				}
			}

			if (!output || typeof output !== 'object') {
				throw new Error(
					`${walletName} wallet does not support Sign In With Aptos (SIWA)`,
				);
			}

			try {
				await loginWithSignInOutput(output, input);

				const signedInAddress = output.account?.address
					? String(output.account.address)
					: '';

				if (!signedInAddress) {
					throw new Error(
						'No wallet address available after sign-in',
					);
				}

				const { id } = await createOrGetUser(
					signedInAddress,
					undefined,
					baseUrl,
				);

				// google login and apple login
				analytics.identify(signedInAddress);
				setUserId(id);

				return {
					walletAddress: signedInAddress,
					userId: id,
					actualWalletName: wallet.name,
				};
			} catch (error) {
				disconnect();
				throw error;
			}
		},
		onError: (err: Error) => {
			onLoginEnd?.();

			// user-initiated cancellations or expected redirect – do not spam Slack
			if (
				err.message.includes('User rejected') ||
				err.message.includes('user rejected') ||
				err.message.includes('rejected the request') ||
				err.message.includes('User cancelled') ||
				err.message.includes('User denied') ||
				err.message.includes(
					'Redirecting to Petra Explore for mobile',
				) ||
				err.message.includes('Preparing secure sign-in')
			) {
				return;
			}

			if (err.message.includes('Failed to fetch')) {
				notifySlackError({
					title: 'Please make sure that the backend is running.',
					details: 'Please make sure that the backend is running.',
				});
			} else {
				notifySlackError({
					title: err.message,
					details: err.message,
				});
			}
		},
		onSuccess: (data) => {
			onLoginEnd?.();

			notifySlackSuccess({
				title: `Successfully signed in! ${walletName}`,
				details: 'Successfully signed in!',
				address: data.walletAddress,
			});

			onSuccess?.();
		},
	});

	// final
	const handleClick = () => {
		if (isLoggedIn) {
			logout();
		} else {
			login();
		}
	};

	const buttonLabel = isPending
		? 'Signing in...'
		: safariRequiresPrefetch
		? 'Preparing...'
		: isLoggedIn && walletAddress
		? truncateAddress(walletAddress)
		: walletName;

	return (
		<Button
			onClick={handleClick}
			disabled={isPending || safariRequiresPrefetch}
			variant="contained"
			{...props}
		>
			{buttonLabel}{' '}
		</Button>
	);
}
